
import React, { useState } from 'react';
import { CreditCard, Wrench, Bell, ChevronRight, Home, Calendar, Wallet, ArrowUpRight, AlertTriangle, FileText, Clock } from 'lucide-react';
import { useTenantScope } from '../../hooks/useTenantScope';
import { useAppStore } from '../../store';
import { MaintenanceRequestForm } from '../maintenance/MaintenanceRequestForm';
import { useNavigate, Navigate } from '../../routes';
import { useNavigateTo } from '@/utils/typeSafeNavigation';
import { formatDate, formatCurrency, getUnitDisplayString, logger } from '@repo/shared-utils';
import { getAppNow } from '@/utils/demoClock';
import { useCurrentTenantId } from '../../hooks/queries/useTenantId';
import { useAnnouncements } from '../../hooks/queries/usePortal';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { PORTAL_EMPTY_STATES } from '@/constants/portalEmptyStates';

export const TenantDashboardView = () => {
  const navigate = useNavigate();
  const navigateTo = useNavigateTo();
  // Access global currentUserId instead of hardcoding
  const currentUserId = useAppStore(state => state.currentUserId);
  const { data: tenantId } = useCurrentTenantId();
  const { data: announcements = [] } = useAnnouncements();
  const currentTenantId = tenantId || currentUserId; // Fallback for backward compatibility

  const { scope, isLoading } = useTenantScope(currentTenantId);

  // All hooks must be called before early return
  const [isMaintenanceFormOpen, setIsMaintenanceFormOpen] = useState(false);

  // Check for incomplete lease renewal draft
  // Move localStorage access to useEffect to avoid impure function calls during render
  const [hasLeaseRenewalDraft, setHasLeaseRenewalDraft] = useState<{ hasDraft: boolean; lastSaved: string | null; step: number | null }>({ hasDraft: false, lastSaved: null, step: null });

  React.useEffect(() => {
    try {
      // Check for any step draft (steps 2, 3, or 4)
      for (let step = 2; step <= 4; step++) {
        const draftKey = `form-draft-tenant-lease-wizard-step-${step}`;
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          const metaKey = `${draftKey}-meta`;
          const meta = localStorage.getItem(metaKey);
          if (meta) {
            const metaData = JSON.parse(meta);
            const savedAt = new Date(metaData.savedAt);
            // Only show if draft is less than 30 days old
            if (Date.now() - savedAt.getTime() < 30 * 24 * 60 * 60 * 1000) {
              setHasLeaseRenewalDraft({ hasDraft: true, lastSaved: metaData.savedAt, step });
              return;
            }
          }
        }
      }
      setHasLeaseRenewalDraft({ hasDraft: false, lastSaved: null, step: null });
    } catch (err) {
      logger.debug('[TenantDashboardView] lease renewal draft check failed', err);
      setHasLeaseRenewalDraft({ hasDraft: false, lastSaved: null, step: null });
    }
  }, []);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-slate-500">{PORTAL_EMPTY_STATES.resident.loadingProfile}</div>
    );
  }
  if (!scope) {
    return <Navigate to={ROUTES.COMPLETE_ONBOARDING} replace />;
  }

  const { profile, lease, financials, maintenance } = scope;

  // Calculate Days Remaining. `getAppNow()` pins the demo viewer's "today" to
  // SEED_ANCHOR_DATE so a lease ending in 2026 doesn't read as "already expired"
  // when the real wall clock drifts past the seed snapshot.
  const leaseEndDate = new Date(lease.dates.end);
  const today = getAppNow();
  const daysRemaining = Math.ceil((leaseEndDate.getTime() - today.getTime()) / (1000 * 3600 * 24));

  // Friendly unit label: "Unit 4B" for multifamily, property-address-only for single-family.
  const unitLabel: string = (() => {
    if (!lease.unit || !lease.property) return '';
    const explicit = lease.unit.unitNumber?.trim();
    if (explicit) return `Unit ${explicit}`;
    const derived = getUnitDisplayString(lease.unit.id, lease.property.type);
    return derived ? `Unit ${derived}` : '';
  })();

  // Get Latest Activity
  const recentPayment = financials.history.length > 0 ? financials.history[0] : null;
  const activeTicket = maintenance.open.length > 0 ? maintenance.open[0] : null;

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Welcome Home, {profile.name.split(' ')[0]}</h1>
          <p className="text-slate-500 dark:text-slate-400">
            {[unitLabel, lease.property?.name].filter(Boolean).join(' | ')}
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex space-x-3">
          <button
            onClick={() => setIsMaintenanceFormOpen(true)}
            className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 px-4 py-2 rounded-lg font-medium shadow-sm transition flex items-center"
          >
            <Wrench size={18} className="mr-2" /> Request Fix
          </button>
          <button
            onClick={() => navigateTo('/app/my-payments')}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition flex items-center"
          >
            <CreditCard size={18} className="mr-2" /> Pay Rent
          </button>
        </div>
      </div>

      {/* Lease Renewal Draft Banner */}
      {hasLeaseRenewalDraft.hasDraft && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <FileText size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="font-bold text-amber-900 dark:text-amber-300">Incomplete Lease Renewal</p>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                You started renewing your lease {hasLeaseRenewalDraft.lastSaved && formatDate(new Date(hasLeaseRenewalDraft.lastSaved), 'short')}.
                {hasLeaseRenewalDraft.step && ` You were on step ${hasLeaseRenewalDraft.step} of 4.`}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/app/lease-renewal')}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold transition flex items-center"
          >
            <Clock size={16} className="mr-2" />
            Resume Renewal
          </button>
        </div>
      )}

      {/* F-pattern: #1 KPI top-left = Balance / payment status; then Lease, then Community News. Traffic-light: green = paid/healthy, amber = attention, red = overdue only. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Balance Card — #1 KPI; neutral card, status badge only uses green (paid) or red (payment due) */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-500 dark:text-slate-400 font-medium text-sm uppercase tracking-wider">Current Balance</h3>
            <Wallet size={20} className="text-slate-400" aria-hidden />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{formatCurrency(financials.balance)}</div>

          {financials.balance === 0 ? (
            <div className="flex items-center text-xs font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-full w-fit border border-emerald-200 dark:border-emerald-800">
              <Calendar size={12} className="mr-1.5" /> Paid for {formatDate(getAppNow(), 'long').split(',')[0]}
            </div>
          ) : (
            <div className="flex items-center text-xs font-bold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-full w-fit border border-red-200 dark:border-red-800">
              <AlertTriangle size={12} className="mr-1.5" /> Payment Due
            </div>
          )}

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            Monthly Rent: {formatCurrency(lease.unit?.rent || 0, { showCents: false })}
          </p>
        </div>

        {/* Lease Status — traffic-light: green when &gt;=90 days left, amber when expiring soon */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-slate-500 dark:text-slate-400 font-medium text-sm uppercase tracking-wider">Lease Status</h3>
              <Home size={20} className={daysRemaining >= 90 ? 'text-emerald-500' : 'text-amber-500'} aria-hidden />
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600 dark:text-slate-300">Term Ends</span>
              <span className="text-sm font-bold text-slate-900 dark:text-white">{formatDate(lease.dates.end, 'medium')}</span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-600 dark:text-slate-300">Status</span>
              <span className="text-sm font-bold text-slate-900 dark:text-white">{lease.status}</span>
            </div>
          </div>
          <div>
            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 mb-2">
              <div className={`h-2 rounded-full ${daysRemaining < 90 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.max(0, Math.min(100, (daysRemaining / 365) * 100))}%` }} />
            </div>
            <p className={`text-xs text-right ${daysRemaining < 90 ? 'text-amber-600 dark:text-amber-400 font-bold' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {daysRemaining} days remaining
            </p>
          </div>
        </div>

        {/* Community News — neutral card (no decorative purple/indigo); traffic-light not used for content */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center mb-4">
            <Bell size={20} className="mr-2 text-slate-400" aria-hidden />
            <h3 className="font-bold text-slate-900 dark:text-white">Community News</h3>
          </div>
          <div className="space-y-3">
            {announcements.slice(0, 2).map((announcement) => (
              <button
                key={announcement.id}
                type="button"
                onClick={() => navigateTo('/app/community')}
                className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer w-full text-left"
              >
                <div className="flex justify-between items-start">
                  <p className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">{announcement.title}</p>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                    {formatDate(announcement.date, 'short')}
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">{announcement.content}</p>
              </button>
            ))}
            {announcements.length === 0 && (
              <div className="p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400">{PORTAL_EMPTY_STATES.tenant.noCommunityAnnouncements}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
          <h3 className="font-bold text-lg text-slate-900 dark:text-white">Recent Activity</h3>
          <button className="text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline flex items-center">
            View All <ArrowUpRight size={14} className="ml-1" />
          </button>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {/* Dynamic Recent Payment */}
          {recentPayment && (
            <button
              type="button"
              className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer group w-full text-left border-0 bg-transparent"
              onClick={() => navigate('/app/my-payments')}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mr-4 border border-emerald-200 dark:border-emerald-900/50">
                  <CreditCard size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition">Recent Payment</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(recentPayment.date, 'medium')} | {recentPayment.paymentMethod}</p>
                </div>
              </div>
              <span className="text-sm font-bold text-slate-900 dark:text-white">-{formatCurrency(Math.abs(recentPayment.amountCents))}</span>
            </button>
          )}

          {/* Dynamic Open Ticket */}
          {activeTicket && (
            <button
              type="button"
              className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer group w-full text-left border-0 bg-transparent"
              onClick={() => navigateTo('/app/maintenance')}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 mr-4 border border-amber-200 dark:border-amber-900/50">
                  <Wrench size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition">Work Order: {activeTicket.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(activeTicket.createdAt, 'medium')} | {activeTicket.status}</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
            </button>
          )}

          {!recentPayment && !activeTicket && (
            <div className="p-8 text-center text-slate-500 text-sm">{PORTAL_EMPTY_STATES.tenant.noRecentActivity}</div>
          )}
        </div>
      </div>

      {/* Maintenance Request Form Modal */}
      {isMaintenanceFormOpen && (
        <MaintenanceRequestForm
          onClose={() => setIsMaintenanceFormOpen(false)}
          onSuccess={() => {
            // Optionally refresh data or show success message
          }}
        />
      )}
    </div>
  )
}
